import { cn } from '@app/utils/cn';
import { useResizeObserver } from '@app/utils/hooks';
import { isDefined } from '@app/utils/is';
import React from 'react';

import {
  useMatrixColumnsContext,
  useMatrixDistributionContext,
  useMatrixPrepackContext,
  useMatrixQuantitiesContext,
  useMatrixSettingsContext,
} from '../context';

import { ControlsHeader } from './ControlsHeader';

export const MatrixOverflow = ({ children }: React.WithChildren) => {
  const { setCanPinColumns, setPixelsToScrollEnd } = useMatrixColumnsContext();
  const { cellQuantitiesMap } = useMatrixQuantitiesContext();
  const { visiblePrepacksLength } = useMatrixPrepackContext();
  const { isDistributionPopupOpen, isEnteringDistributionQuantities } = useMatrixDistributionContext();
  const { isMobile } = useMatrixSettingsContext();
  const wrapperRef = React.useRef<HTMLDivElement | null>(null);
  const scrollContainerRef = React.useRef<HTMLDivElement | null>(null);

  const updateIsOverflowing = React.useCallback(() => {
    const scrollContainer = scrollContainerRef.current;

    if (!isDefined(scrollContainer)) {
      return;
    }

    setCanPinColumns(scrollContainer.scrollWidth > scrollContainer.clientWidth);
  }, [setCanPinColumns]);

  const updateHasMoreContent = React.useCallback(() => {
    const scrollContainer = scrollContainerRef.current;

    if (!isDefined(scrollContainer)) {
      return;
    }

    setPixelsToScrollEnd(scrollContainer.scrollWidth - scrollContainer.clientWidth - scrollContainer.scrollLeft);
  }, [setPixelsToScrollEnd]);

  const handleResize = React.useCallback(() => {
    updateIsOverflowing();
    updateHasMoreContent();
  }, [updateHasMoreContent, updateIsOverflowing]);

  useResizeObserver(scrollContainerRef, handleResize);

  React.useEffect(() => {
    handleResize();
  }, [handleResize, cellQuantitiesMap, visiblePrepacksLength]);

  React.useEffect(() => {
    if (!isDistributionPopupOpen || !isMobile || !isDefined(wrapperRef.current)) {
      return;
    }

    wrapperRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [isDistributionPopupOpen, isMobile, isEnteringDistributionQuantities]);

  return (
    <div data-testid="size-matrix">
      <ControlsHeader />
      <div ref={wrapperRef} className={cn({ 'relative z-overlay scroll-mt-32': isDistributionPopupOpen && isMobile })}>
        <div onScroll={updateHasMoreContent} ref={scrollContainerRef} className="overflow-x-auto overflow-y-hidden overflow-scrolling">
          {children}
          {isDistributionPopupOpen && isMobile && (
            <div className="absolute inset-0 -top-2 -z-elevated h-full w-full rounded-regular bg-white sm:hidden" />
          )}
        </div>
      </div>
    </div>
  );
};
